import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api, errorText } from '../api/client'
import type { ContactProfile } from '../api/types'
import { ConsentModal } from '../components/ConsentModal'
import { ExportToLosModal } from '../components/ExportToLosModal'
import { Icon } from '../components/Icon'
import { OUTREACH, reachable, useOutreach } from '../components/Outreach'
import { EnrollmentList, PlaybookModal } from '../components/Playbooks'
import { ErrorState, Loading } from '../components/States'
import { useToast } from '../components/Toast'
import { Timeline } from '../components/Timeline'
import { equityIsVerified, equityOf } from '../lib/equity'
import { initials, phoneText, titleCase, usd } from '../lib/format'
import { tint } from '../lib/tint'
import { useAsync } from '../lib/useAsync'
import { RankPill, StatusChip } from './Leads'

/** The lead platform's "why this lead" payload, as `explain_lead` writes it.
 *  Everything is optional: a lead scored before a field existed simply has none,
 *  and a missing card is a card that does not render rather than an error. */
interface Explain {
  provider?: string | null
  score?: number | null
  campaign?: string | null
  reasons?: { factor: string; value: string; detail: string; impact: string }[]
  sources?: Record<string, string | null>
  recorded_loan?: Record<string, unknown> | null
  rate?: { est?: string; today?: string; gap?: string; note?: string } | null
  arm_rider?: Record<string, unknown> | null
}

/** Why the handoff is off, in the words of the thing that would turn it on. */
const EXPORT_NEEDS = 'A file needs a loan purpose and an amount. Add them under Loan goal, or open an opportunity for them.'

const IMPACT_COLOR: Record<string, string> = {
  high: 'var(--positive)', med: 'var(--warning)', low: 'var(--accent)', info: 'var(--ink-4)',
}

/** How well the skip-traced contact matched the owner of record. `low` is the one
 *  that matters: it means the name on the phone is not the name on the deed, so
 *  the person who answers may not be the owner at all. */
/** Who the lead is, taken from the campaign that selected them. This is not a
 *  guess: the Purchase and Refinance selectors only ever pull owner-occupied
 *  SF/TH/CN parcels, and the Rentals campaign mails the *resident* of a licensed
 *  rental, who by definition does not own it (see outreach/select.py). Any other
 *  campaign, or none, says nothing. */
function persona(campaign: string | null): string | null {
  const c = (campaign ?? '').toLowerCase()
  if (c.includes('rental')) return 'Renter'
  if (c.includes('refinance') || c.includes('purchase') || c.includes('equity')) return 'Homeowner'
  return null
}

const RIGHT_PARTY: Record<string, { label: string; tone: string }> = {
  high: { label: 'Owner matched', tone: 'positive' },
  medium: { label: 'Partial match', tone: 'warning' },
  low: { label: 'Name mismatch', tone: 'danger' },
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}
function str(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v.trim() : null
}

/** One lead, as the lead platform shows it: the property and equity facts on the
 *  left, what may be done about it on the right. The CRM's contact card answers
 *  "who is this person"; this page answers "is this one worth the call", which is
 *  a different question with a different set of numbers behind it. */
export function LeadDetail() {
  const { contactId } = useParams()
  const id = Number(contactId)
  const [logged] = useState(0)
  const [exporting, setExporting] = useState(false)
  const [consenting, setConsenting] = useState(false)
  const [playbook, setPlaybook] = useState(false)
  const [bump, setBump] = useState(0)
  const { data, error, loading, reload } = useAsync(() => api.contact(id), [id])
  // One wiring for both pages: same channels, same order, same dialogs. Declared
  // before the loading guard, because a hook cannot be conditional.
  const outreach = useOutreach(id, data?.full_name ?? 'this lead', {
    phone: data?.methods.find((m) => m.kind === 'mobile' || m.kind === 'landline')?.value,
    onChanged: reload,
  })

  if (loading && !data) return <div className="page"><Loading label="Loading the lead…" /></div>
  if (error || !data) return <div className="page"><ErrorState error={error} onRetry={reload} /></div>

  const lead = data
  const s = (lead.signals ?? {}) as Record<string, unknown>
  const xp = (lead.explain ?? {}) as Explain
  const place = [lead.address, lead.city, lead.state].filter(Boolean).join(', ')
  const rp = lead.right_party ? RIGHT_PARTY[lead.right_party] : null
  const phones = lead.methods.filter((m) => m.kind === 'mobile' || m.kind === 'landline')
  const emails = lead.methods.filter((m) => m.kind === 'email')
  const withheld = phones.filter((p) => p.dnc).length
  // A campaign lead carries its own purpose (the recommended product), so most
  // leads are exportable without anyone filling the intake in.
  const canExport = Boolean(lead.product || lead.profile?.purpose
    || lead.profile?.loan_amount || lead.deals?.length)


  return (
    <div className="page">
      <Link to="/leads" className="backlink"><Icon name="arrow_back" size={16} /> Back to leads</Link>

      <div className="lead-head">
        <RankPill rank={lead.rank} />
        <h1 className="h1" style={{ margin: 0 }}>{lead.full_name ?? 'Unnamed lead'}</h1>
        {persona(lead.campaign) ? (
          <span className="persona" title="From the campaign that selected them: the purchase and refinance pulls are owner-occupied only, and the rentals pull mails the resident.">
            <Icon name={persona(lead.campaign) === 'Renter' ? 'apartment' : 'home'} size={14} />
            {persona(lead.campaign)}
          </span>
        ) : null}
        <span className="spacer" />
        {lead.campaign ? <span className="camp-tag">{lead.campaign}</span> : null}
        <StatusChip status={lead.status} />
        {/* Nothing to build from means nothing to open the dialog for. The reason
            is on the hover rather than discovered inside an empty form. */}
        <button className="btn los" onClick={() => setExporting(true)} disabled={!canExport}
          title={canExport ? 'Build a MISMO 3.4 file from this lead' : EXPORT_NEEDS}>
          <Icon name="upload_file" size={16} /> To LendingPad
        </button>
      </div>
      <p className="subline" style={{ marginTop: 2 }}>
        {place || 'No property address on file'}
        {lead.product ? ` · ${titleCase(lead.product)}` : ''}
      </p>

      {/* The house they are buying, which is not the one on the line above — that
          is where they live now. Quiet until it is wanted: a link, then four boxes. */}
      <BuyingAddress contactId={id} profile={lead.profile ?? null} onSaved={reload} />

      {lead.right_party === 'low' ? (
        <div className="banner warn">
          <Icon name="warning" size={18} />
          <div>
            <b>Contact unverified.</b> The skip trace returned a name that does not match the owner
            of record, so whoever answers may not own this property. Confirm who you are speaking to
            before discussing the loan.
          </div>
        </div>
      ) : null}

      <div className="lead-grid">
        <div>
          <div className="metric-grid">
            <Metric label="Assessed value" value={usd(num(s.assessed))} />
            <Metric label="Est. equity" value={usd(num(s.equity_est))}
              badge={equityIsVerified(lead)
                ? { text: `${str(xp.provider) ?? 'Recorded'}-verified`, tone: 'positive' }
                : { text: 'our estimate', tone: 'warning' }} />
            <Metric label="Usable equity" value={usd(equityOf(lead))}
              hint="Net of the cushion they could not borrow against." />
            <Metric label="Recommended" value={lead.product ? titleCase(lead.product) : '—'} />
            <Metric label="Rate window" value={str(s.rate_window) ?? '—'}
              badge={num(s.rate_gap) !== null && num(s.rate_gap)! > 0
                ? { text: 'above market', tone: 'warning' } : undefined} />
            <Metric label="Years held" value={num(s.years_held) !== null ? `${Math.round(num(s.years_held)!)} yrs` : '—'} />
            <Metric label="Year built" value={num(s.year_built) !== null ? String(num(s.year_built)) : '—'} />
            <Metric label="Right-party" value={rp
              ? <span className={`badge t-${rp.tone}`}>{rp.label}</span>
              : '—'} />
          </div>

          <section className="card">
            <div className="card-head">
              <h2 className="h2">Why this lead</h2>
              <span className="spacer" />
              <span className="quiet" style={{ fontSize: 12 }}>
                {xp.score != null ? `score ${Math.round(xp.score)} · what drives it` : 'what drives it'}
              </span>
            </div>
            <div className="card-body">
              {xp.rate?.gap ? (
                <div className="rate-compare">
                  <span><em>their rate</em><b>{xp.rate.est ?? '—'}</b></span>
                  <Icon name="arrow_forward" size={16} />
                  <span><em>today</em><b>{xp.rate.today ?? '—'}</b></span>
                  <span className="rc-gap">{xp.rate.gap}</span>
                  {xp.rate.note ? <span className="quiet rc-note">{xp.rate.note}</span> : null}
                </div>
              ) : null}

              {(xp.reasons ?? []).length === 0 ? (
                <p className="quiet" style={{ margin: 0 }}>
                  No scoring detail was stored for this lead — it predates the explain payload, or
                  came in by hand rather than from a campaign pull.
                </p>
              ) : (xp.reasons ?? []).map((r, i) => (
                <div className="reason" key={`${r.factor}-${i}`}>
                  <span className="reason-dot" style={{ background: IMPACT_COLOR[r.impact] ?? IMPACT_COLOR.info }} />
                  <div>
                    <div className="reason-factor">{r.factor} <b>{r.value}</b></div>
                    <div className="reason-detail">{r.detail}</div>
                  </div>
                </div>
              ))}

              {/* Which numbers above are measured and which are modelled. An
                  officer about to quote equity on the phone needs to know. */}
              {Object.keys(xp.sources ?? {}).length ? (
                <div className="sources">
                  <span className="sl">Data</span>
                  {Object.entries(xp.sources!).map(([k, v]) => {
                    // A source of null is a figure nobody measured, and it used to
                    // print as "null-verified" beside a number an officer was about
                    // to quote. No source means modelled, and it says so.
                    const from = str(v)
                    const real = !!from && from !== 'amortized' && from !== 'sdat_bucket'
                    return (
                      <span key={k} className={`badge t-${real ? 'positive' : 'warning'}`}>
                        {k}: {real ? `${str(xp.provider) ?? from}-verified` : 'estimated'}
                      </span>
                    )
                  })}
                </div>
              ) : null}
            </div>
          </section>

          {xp.recorded_loan ? <FactsCard title="Recorded loan" facts={xp.recorded_loan}
            note={`Loan facts from county deed-of-trust records via ${xp.provider ?? 'the data provider'}.`} /> : null}
          {xp.arm_rider ? <FactsCard title="ARM rider terms" facts={xp.arm_rider}
            note="Rider terms from the recorded adjustable loan: the rate resets to index plus margin, capped for the life of the loan." /> : null}

          <section className="card">
            <div className="card-head"><h2 className="h2">Activity</h2></div>
            <div className="card-body">
              <Timeline subject={{ contact_id: id }} refreshKey={logged}
                emptyText="Nothing logged against this lead yet." />
            </div>
          </section>
        </div>

        <div className="lead-rail">
          <section className="card">
            <div className="action-bar">
              {OUTREACH.map((c) => {
                const { ok, why } = reachable(lead.channels?.[c.key])
                return (
                  <button key={c.key} className={`act${ok ? '' : ' locked'}`} disabled={!ok}
                    title={ok ? c.label : why} onClick={() => outreach.start(c.key)}>
                    {ok ? null : <Icon name="lock" size={11} className="lockmark" />}
                    <Icon name={c.icon} size={18} />
                    <span>{c.label}</span>
                  </button>
                )
              })}
            </div>
            {!lead.channels?.sms?.allowed ? (
              <div className="card-body" style={{ paddingTop: 0 }}>
                <div className="banner warn" style={{ margin: 0 }}>
                  <Icon name="shield" size={18} />
                  <div>
                    {lead.channels?.sms?.reason || 'Texting is not available.'}{' '}
                    {/* Only true when the block is a missing opt-in. A number on the
                        registry blocks the call as well, so saying calls are fine
                        there would be the one sentence that gets somebody fined. */}
                    {lead.channels?.call?.allowed
                      ? 'Mail and calls need no consent, so both are still open.'
                      : 'Direct mail needs no consent and is still open.'}
                  </div>
                </div>
              </div>
            ) : null}
          </section>

          <section className="card">
            <div className="card-head">
              <h2 className="h2">Workflows</h2>
              <span className="spacer" />
              <button className="btn sm" onClick={() => setPlaybook(true)}
                title="Start one of your workflows for this lead">
                <Icon name="add" size={15} /> Add
              </button>
            </div>
            <div className="card-body" style={{ paddingTop: 4 }}>
              <EnrollmentList subject={{ contact_id: id }} refreshKey={bump}
                onChange={() => setBump((n) => n + 1)} onAdd={() => setPlaybook(true)} />
            </div>
          </section>

          <section className="card">
            <div className="rail-h">
              Contact — enriched
              {withheld ? <span className="quiet"> · {withheld} of {phones.length} withheld (DNC)</span> : null}
            </div>
            {lead.methods.length === 0 ? (
              <div className="card-body"><p className="quiet" style={{ margin: 0 }}>Nothing on file yet.</p></div>
            ) : null}
            {phones.map((p) => (
              <div className="contact-row" key={`${p.kind}-${p.value}`}>
                <Icon name="call" size={17} className="ci" />
                <div style={{ minWidth: 0 }}>
                  <div className={`cv${p.dnc ? ' is-dnc' : ''}`}>{p.dnc ? '••• ••• ••••' : phoneText(p.value)}</div>
                  <div className="cl">{titleCase(p.kind)}{p.dnc ? ' · withheld (DNC)' : ''}</div>
                </div>
                {!p.dnc && p.confidence
                  ? <span className={`badge t-${p.confidence === 'high' ? 'positive' : 'neutral'}`}>{p.confidence}</span>
                  : null}
              </div>
            ))}
            {emails.map((m) => (
              <div className="contact-row" key={m.value}>
                <Icon name="alternate_email" size={17} className="ci" />
                <div style={{ minWidth: 0 }}>
                  <div className="cv">{m.value}</div>
                  <div className="cl">Email</div>
                </div>
              </div>
            ))}
          </section>

          <section className="card">
            <div className="rail-h">Consent &amp; suppression</div>
            <ConsentLine label="Do-Not-Call" ok={!withheld}
              yes="Clear (scrubbed)" no="On DNC — no calls or texts" />
            <ConsentLine label="SMS opt-in (TCPA)" ok={lead.consent.sms?.status === 'opted_in'}
              yes={`Opted in${lead.consent.sms?.basis ? ` · ${lead.consent.sms.basis.replace(/_/g, ' ')}` : ''}`}
              no={lead.consent.sms?.status === 'opted_out' ? 'Replied STOP' : 'Not captured'} />
            <ConsentLine label="WhatsApp opt-in" ok={lead.consent.whatsapp?.status === 'opted_in'}
              yes="Opted in" no="Not captured" />
            <ConsentLine label="Email unsubscribe" ok={lead.consent.email?.status !== 'opted_out'}
              yes="Not opted out" no="Opted out · suppressed" />
            {/* The way to turn the text button back on, next to the line that says
                why it is off. An opt-in is a fact about the file, so it is recorded
                here rather than half-way through writing a message. */}
            <div className="card-body" style={{ paddingTop: 2 }}>
              <button className="linklike" onClick={() => setConsenting(true)}>
                {lead.consent.sms?.status === 'opted_in'
                  ? 'Withdraw or remove it' : 'Record their opt-in'}
              </button>
            </div>
          </section>

          <section className="card">
            <div className="rail-h">Assignment</div>
            <div className="consent-line">
              <span className="cn">Loan officer</span>
              {lead.owner ? (
                <span style={{ display: 'flex', gap: 7, alignItems: 'center' }}>
                  <span className="gt-av" style={{ ...tint(lead.owner), width: 24, height: 24, fontSize: 10 }}>
                    {initials(lead.owner)}
                  </span>
                  {lead.owner}
                </span>
              ) : <span className="badge t-warning">Unassigned</span>}
            </div>
            <div className="consent-line">
              <span className="cn">Full contact record</span>
              <Link className="btn sm" to={`/contacts/c/${id}`}>Open contact card</Link>
            </div>
          </section>
        </div>
      </div>

      <PlaybookModal open={playbook} onClose={() => setPlaybook(false)}
        subject={{ contact_id: id }} onEnrolled={() => { setBump((n) => n + 1); reload() }}
        firstName={lead.first_name ?? (lead.full_name ?? '').split(' ')[0]} />

      {outreach.modals}

      {consenting ? (
        <ConsentModal contactId={id} name={lead.full_name ?? 'this lead'} channel="sms"
          current={lead.consent.sms?.status}
          onClose={() => setConsenting(false)} onSaved={reload} />
      ) : null}

      {exporting ? (
        <ExportToLosModal subject={{ contact_id: id }} onClose={() => setExporting(false)}
          onPushed={() => { setExporting(false); reload() }} />
      ) : null}

    </div>
  )
}

function Metric({ label, value, badge, hint }: {
  label: string
  value: React.ReactNode
  badge?: { text: string; tone: string }
  hint?: string
}) {
  return (
    <div className="metric" title={hint}>
      <div className="ml">
        {label}
        {badge ? <span className={`badge t-${badge.tone}`} style={{ marginLeft: 6 }}>{badge.text}</span> : null}
      </div>
      <div className="mv">{value}</div>
    </div>
  )
}

/** A card of recorded facts straight from the explain payload. The keys are
 *  whatever `explain_lead` put there, so they are titled rather than mapped —
 *  a new field added upstream shows up here instead of silently vanishing. */
function FactsCard({ title, facts, note }: {
  title: string; facts: Record<string, unknown>; note: string
}) {
  const entries = Object.entries(facts).filter(([, v]) => v !== null && v !== '' && typeof v !== 'object')
  if (!entries.length) return null
  return (
    <section className="card">
      <div className="card-head"><h2 className="h2">{title}</h2></div>
      <div className="card-body">
        <div className="loan-grid">
          {entries.map(([k, v]) => (
            <div key={k}>
              <div className="ll">{titleCase(k.replace(/_/g, ' '))}</div>
              <div className="lv">{String(v)}</div>
            </div>
          ))}
        </div>
        <p className="quiet" style={{ fontSize: 11.5, margin: '12px 0 0' }}>{note}</p>
      </div>
    </section>
  )
}

/** Where they're moving to, on a lead who is buying.
 *
 *  It lives on the intake rather than on the contact because the contact's address
 *  is where they live today — on a purchase those are two different properties, and
 *  the export has to know which is which. Once it's here the 3.4 file picks it up
 *  with nothing typed into the export dialog. */
function BuyingAddress({ contactId, profile, onSaved }: {
  contactId: number
  profile: Partial<ContactProfile> | null
  onSaved: () => void
}) {
  const saved = [profile?.property_street, profile?.property_city,
                 profile?.property_state].filter(Boolean).join(', ')
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState({
    property_street: profile?.property_street ?? '', property_city: profile?.property_city ?? '',
    property_state: profile?.property_state ?? '', property_zip: profile?.property_zip ?? '',
  })
  const [busy, setBusy] = useState(false)
  const toast = useToast()

  async function save() {
    setBusy(true)
    try {
      await api.saveProfile(contactId, form)
      toast('Purchase address saved', { tone: 'positive' })
      setOpen(false)
      onSaved()
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
    } finally {
      setBusy(false)
    }
  }

  const box = (key: keyof typeof form, label: string) => (
    <label key={key} className="fl">
      <span>{label}</span>
      <input className="field" value={form[key]}
        onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} />
    </label>
  )

  if (!open) {
    return (
      <p className="buying-line">
        {saved ? <><Icon name="key" size={14} /> Buying <strong>{saved}</strong></>
          : <Icon name="key" size={14} />}
        <button className="linklike" onClick={() => setOpen(true)}>
          {saved ? 'Change it' : 'Add the address they’re buying'}
        </button>
      </p>
    )
  }
  return (
    <div className="card buying-card">
      <div className="card-body">
        <div className="form-grid">
          {box('property_street', 'Street')}
          {box('property_city', 'City')}
          {box('property_state', 'State')}
          {box('property_zip', 'ZIP')}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button className="btn primary" onClick={save} disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
          <button className="btn" onClick={() => setOpen(false)}>Cancel</button>
        </div>
      </div>
    </div>
  )
}

function ConsentLine({ label, ok, yes, no }: { label: string; ok: boolean; yes: string; no: string }) {
  return (
    <div className="consent-line">
      <span className="cn">{label}</span>
      <span className={`badge t-${ok ? 'positive' : 'neutral'}`}>{ok ? yes : no}</span>
    </div>
  )
}
